import React, {memo, useCallback, useEffect, useMemo} from 'react';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {useLazyRef} from '../../helpers/hookHelpers';
import {RelativeDateTimeContextProps, RelativeDateTimeContextProvider} from './relativeDateTimeContext';

/*
 * Component.
 */

export const RelativeDateTimeProvider = memo(({children}) => {
  const refreshersRef = useLazyRef(() => new Set<Handler>());

  /*
   * Call all refreshers periodically.
   */

  useEffect(() => {
    const interval = setInterval(() => {
      refreshersRef.current.forEach((refresher) => refresher());
    }, 30 * 1000); // 30s.
    return () => clearInterval(interval);
  }, [refreshersRef]);

  /*
   * Provide a context to register a refresher.
   */

  const registerRefresher = useCallback(
    (refresher: Handler) => {
      refreshersRef.current.add(refresher);
      return () => refreshersRef.current.delete(refresher);
    },
    [refreshersRef],
  );

  const context = useMemo<RelativeDateTimeContextProps>(() => ({registerRefresher}), [registerRefresher]);

  /*
   * Render.
   */

  return <RelativeDateTimeContextProvider value={context}>{children}</RelativeDateTimeContextProvider>;
});
